import { useEffect, useId, useRef } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { Button } from "../../app/components/primitives/button";
import { X } from "lucide-react";

// The short sheet behind a phone's slim bottom bar, shared by the quote's decision area and the order's summary
// (QuoteDecision.tsx, OrderSummaryPanel.tsx).

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** A bottom sheet: modal, focus moves in and is kept in, Escape closes it, focus goes back to what opened it. */
export function BottomSheet({
  title,
  onClose,
  returnFocusTo,
  children,
}: {
  title: string;
  onClose: () => void;
  /** What opened the sheet: focus goes back to it on close. */
  returnFocusTo: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const capabilities = usePlatformCapabilities();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const opener = returnFocusTo.current;
    const unlockScroll = capabilities.lockPageScroll();
    headingRef.current?.focus();
    return () => {
      unlockScroll();
      opener?.focus();
    };
  }, [capabilities, returnFocusTo]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const stops = [
      ...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    ];
    if (stops.length === 0) return;
    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = e.target;
    if (e.shiftKey && (active === first || active === headingRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="fixed inset-0 z-30" onKeyDown={onKeyDown}>
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t bg-card p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-xl"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold focus:outline-none"
          >
            {title}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}
